// src/components/nav-user.tsx
//
// FIXED — DropdownMenuTrigger previously wrapped SidebarMenuButton via
// render={<SidebarMenuButton .../>}, a double useRender/mergeProps
// composition (DropdownMenuTrigger's render → SidebarMenuButton's own
// internal useRender call). Per Base UI's own composition docs
// (base-ui.com/react/handbook/composition), a component passed to
// render must correctly forward the ref and spread all received props
// onto its underlying DOM node — SidebarMenuButton's second internal
// useRender/mergeProps pass was the likely point where that forwarding
// broke down, losing track of the anchor element Menu.Trigger needs.
// Matches a known, documented Base UI failure class (mui/base-ui#3184):
// trigger open/close state getting confused when the rendered element
// doesn't cleanly match what the trigger captured as its own anchor —
// symptom was the menu opening and instantly closing on every click.
//
// Fix: DropdownMenuTrigger now renders its own default button directly
// (no render prop, no second component in the chain) and takes
// SidebarMenuButton's CLASSES via sidebarMenuButtonVariants() instead
// of the component itself. Visually identical, but only one render
// layer between Menu.Trigger and the actual DOM node — matches Base
// UI's own example pattern of nesting a single custom component
// directly, not a component that itself re-renders through another
// primitive.
//
// Trade-off, noted rather than silently accepted: this loses
// SidebarMenuButton's tooltip-when-collapsed behavior (the `tooltip`
// prop branch in sidebar.tsx). Not reproduced here — if the sidebar's
// collapsed/icon-only state needs a tooltip on this trigger later,
// that's separate follow-up work, not bundled into this fix.

import {
  Avatar, AvatarFallback, AvatarImage,
} from './ui/avatar';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  SidebarMenu, SidebarMenuItem, useSidebar, sidebarMenuButtonVariants,
} from './ui/sidebar';
import { cn } from '@/lib/utils';
import { ChevronsUpDownIcon, ShieldCheckIcon, LogOutIcon } from 'lucide-react';
import { UserProfile, ViewState } from '../types';
import { mockSignOut } from '../services/firebase';

interface NavUserProps {
  user: UserProfile;
  onNavigate: (view: ViewState) => void;
}

export function NavUser({ user, onNavigate }: NavUserProps) {
  const { isMobile } = useSidebar();
  const initials = (user.displayName || user.email || '?')
    .split(' ').map((s) => s[0]).join('').slice(0, 2).toUpperCase();

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger
            className={cn(
              sidebarMenuButtonVariants({ size: 'lg' }),
              'md:h-8 md:p-0 data-open:bg-sidebar-accent data-open:text-sidebar-accent-foreground'
            )}
          >
            <Avatar className="h-8 w-8 rounded-lg">
              <AvatarImage src={user.photoURL ?? undefined} alt={user.displayName ?? ''} />
              <AvatarFallback className="rounded-lg">{initials}</AvatarFallback>
            </Avatar>
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span className="truncate font-medium">{user.displayName || 'Account'}</span>
              <span className="truncate text-xs">{user.email}</span>
            </div>
            <ChevronsUpDownIcon className="ml-auto size-4" />
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="min-w-56 rounded-lg"
            side={isMobile ? 'bottom' : 'right'}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuGroup>
              <DropdownMenuLabel className="p-0 font-normal">
                <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                  <Avatar className="h-8 w-8 rounded-lg">
                    <AvatarImage src={user.photoURL ?? undefined} alt={user.displayName ?? ''} />
                    <AvatarFallback className="rounded-lg">{initials}</AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-medium">{user.displayName || 'Account'}</span>
                    <span className="truncate text-xs">{user.email}</span>
                  </div>
                </div>
              </DropdownMenuLabel>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            {user.isAdmin && (
              <>
                <DropdownMenuGroup>
                  <DropdownMenuItem onClick={() => onNavigate('ADMIN')}>
                    <ShieldCheckIcon />
                    Admin Console
                  </DropdownMenuItem>
                </DropdownMenuGroup>
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuItem onClick={() => mockSignOut().then(() => onNavigate('HUB'))}>
              <LogOutIcon />
              Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}